import React, { useState, useEffect } from "react";
import InfinityScroll from "../../common/InfinityScroll";
import { AuctionItemStatus, AuctionStatus, ObjectType } from "./SupplierAuction";
import Item from "./Item";
import styles from "./MyItemsForSale.module.scss";
import GoTopButton from "../../components/GoTopButton";
import { formatPriceWs } from "../../utils/formatPrice";
import getAuctionHub from "../../getAuctionHub";
import suppliersClient from "../../clients/SuppliersClient";
const Limit = "15";
const OnlineAuctionId = 100002;
export function MyItemsForSale({ strings, viewModel, auctionPageUrl }) {
    const displayAllAuctions = { key: 0, value: strings.displayAllAuctions };
    const [auctions, setAuctions] = useState();
    const [items, setItems] = useState([]);
    const [sums, setSums] = useState(null);
    const [objectsType, setObjectsType] = useState(getInitialObjectType(viewModel));
    const [sortOrder, setSortOrder] = useState(getInitialSortOrder(viewModel));
    const [loading, setLoading] = useState(false);
    const [auctionOptions, setAuctionOptions] = useState([]);
    const [selectedAuction, setSelectedAuction] = useState(displayAllAuctions);
    const [nextPageHandle, setNextPageHandle] = useState("");
    const [error, setError] = useState();
    async function fetchAuctionTypesData() {
        setLoading(true);
        const auctionsData = await suppliersClient.getMyAuctions();
        setAuctions(auctionsData);
        setLoading(false);
    }
    async function fetchItems(loadMore = false) {
        var _a;
        setError(undefined);
        if (loadMore && !nextPageHandle)
            return;
        if (!auctions || auctions.length == 0)
            return;
        try {
            setLoading(true);
            const response = await suppliersClient.getMyItems(buildQuery(loadMore ? nextPageHandle : ''));
            const itemsToSet = loadMore ? items.concat(mapItems(response.items)) : mapItems(response.items);
            setItems(itemsToSet);
            setNextPageHandle((_a = response.nextPage) !== null && _a !== void 0 ? _a : "");
        }
        catch (err) {
            setError(strings.rambaseItemsError);
        }
        finally {
            setLoading(false);
        }
    }
    async function getBids() {
        if (!auctions || auctions.length == 0)
            return null;
        try {
            return await suppliersClient.getBids(buildQuery());
        }
        catch (error) {
            return null;
        }
    }
    function onObjectsTypesChange(e) {
        const key = parseInt(e.target.value);
        setObjectsType(viewModel.objectsTypes.find(t => t.key == key));
        sessionStorage.setItem('objectType', key.toString());
    }
    function onSortOrderChange(e) {
        const key = parseInt(e.target.value);
        setSortOrder(viewModel.sortOrders.find(t => t.key == key));
        sessionStorage.setItem('sortOrder', key.toString());
    }
    function onFilterChange(e) {
        const key = parseInt(e.target.value);
        setSelectedAuction(auctionOptions.find(t => t.key == key));
        sessionStorage.setItem('auctionKey', key.toString());
    }
    function mapItems(items) {
        return items.map((itm) => {
            var _a, _b;
            itm.relativeUrl = `${auctionPageUrl}${itm.relativeUrl}`;
            itm.auctionName = (_b = (_a = auctions === null || auctions === void 0 ? void 0 : auctions.find(x => x.id == itm.auctionId)) === null || _a === void 0 ? void 0 : _a.title) !== null && _b !== void 0 ? _b : '';
            return itm;
        });
    }
    function setFilters(withInitialAuction = false) {
        var _a, _b;
        if (!auctions) {
            return;
        }
        const filters = auctions.filter(auction => getAuctionStatuses(objectsType.key).includes(auction.status)).map(x => ({ key: x.id, value: x.title })).sort((a, b) => b.key - a.key); //sort by EVT id desc
        // as requested in SKASD-185 https://fabres.atlassian.net/browse/SKASD-185?focusedCommentId=13091
        // onlineauksjon should be always first option for `Avsluttede auksjoner` and `Ventende objekter`
        const onlineauksjonIndex = filters.findIndex(x => x.key == OnlineAuctionId);
        if (onlineauksjonIndex >= 0) {
            filters.unshift(filters.splice(onlineauksjonIndex, 1)[0]);
        }
        else if ((objectsType.key === 0 || objectsType.key === 2)) {
            filters.unshift({ key: OnlineAuctionId, value: 'Onlineauksjon' });
        }
        filters.unshift(displayAllAuctions);
        setAuctionOptions(filters);
        if (withInitialAuction) {
            const auctionKey = parseInt((_a = sessionStorage.getItem('auctionKey')) !== null && _a !== void 0 ? _a : '-1');
            const objectTypeKey = parseInt((_b = sessionStorage.getItem('objectType')) !== null && _b !== void 0 ? _b : '-1');
            if (auctionKey >= 0 && objectTypeKey >= 0) {
                const auctionToSet = filters.find(f => f.key === auctionKey);
                const validObjectType = objectsType.key === objectTypeKey;
                if (auctionToSet && validObjectType) {
                    setSelectedAuction(auctionToSet);
                    sessionStorage.setItem('auctionKey', auctionToSet.key.toString());
                    return;
                }
            }
        }
        else if (objectsType.key === 2) {
            //SKASD-185 - set 'Slutter sist (key 2)' as default sort order when 'Avsluttede objekter (key 2)' is selected.
            const sortOrderToSet = viewModel.sortOrders.find(so => so.key === 2);
            setSortOrder(sortOrderToSet);
            sessionStorage.setItem('sortOrder', sortOrderToSet.key.toString());
        }
        setSelectedAuction(displayAllAuctions);
        sessionStorage.setItem('auctionKey', displayAllAuctions.key.toString());
    }
    async function updateSums() {
        var _a, _b;
        const auctionId = selectedAuction === null || selectedAuction === void 0 ? void 0 : selectedAuction.key;
        const objectType = objectsType.key;
        let auctionTotals;
        let lotsStatuses = [];
        setSums(null);
        const bidsRes = await getBids();
        //data for all auctions
        if (!auctionId || auctionId === 0) {
            const auctionStatuses = getAuctionStatuses(objectType);
            //exclude Onlineauksjon from filtering since it was added explicitly, even though it's not in the status (auction status) appropriate for selected Object's type
            auctionTotals = (_a = auctions === null || auctions === void 0 ? void 0 : auctions.filter(x => auctionStatuses.includes(x.status) || x.id === OnlineAuctionId).flatMap(auction => auction.totals.totalsByLotStatus.filter(totalsByStatus => getLotStatuses(objectType, auction.status).includes(totalsByStatus.lotStatus)))) !== null && _a !== void 0 ? _a : [];
        }
        //specific auction selected
        else {
            const auctionObject = auctions === null || auctions === void 0 ? void 0 : auctions.find(x => x.id == auctionId);
            lotsStatuses = getLotStatuses(objectType, auctionObject === null || auctionObject === void 0 ? void 0 : auctionObject.status);
            auctionTotals = (_b = auctionObject === null || auctionObject === void 0 ? void 0 : auctionObject.totals.totalsByLotStatus.filter(x => lotsStatuses.includes(x.lotStatus))) !== null && _b !== void 0 ? _b : [];
        }
        const sumsToAdd = auctionTotals.reduce((acc, item) => {
            var _a;
            return ({
                currentBidsSum: acc.currentBidsSum + item.sumOfCurrentBidPrice,
                estimatedSum: acc.estimatedSum + item.sumOfEstimatedPrice,
                numberOfLots: acc.numberOfLots + item.numberOfLots,
                bidsCount: (_a = bidsRes === null || bidsRes === void 0 ? void 0 : bidsRes.count) !== null && _a !== void 0 ? _a : 0,
            });
        }, {
            currentBidsSum: 0,
            estimatedSum: 0,
            numberOfLots: 0,
            bidsCount: null
        });
        setSums(sumsToAdd);
    }
    function buildQuery(nextPageHandle = '') {
        var _a, _b, _c, _d;
        return {
            auctionType: (_c = (_b = (_a = auctions === null || auctions === void 0 ? void 0 : auctions.find(x => x.id == (selectedAuction === null || selectedAuction === void 0 ? void 0 : selectedAuction.key))) === null || _a === void 0 ? void 0 : _a.status) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : "",
            objectType: (_d = objectsType.key) === null || _d === void 0 ? void 0 : _d.toString(),
            sortOrder: sortOrder.key.toString(),
            auctionId: (selectedAuction === null || selectedAuction === void 0 ? void 0 : selectedAuction.key) > 0 ? selectedAuction.key.toString() : null,
            nextPageHandle,
            limit: Limit,
        };
    }
    function getAuctionStatuses(objectType) {
        switch (objectType) {
            case ObjectType.New:
                return [AuctionStatus.New];
            case ObjectType.Active:
                return [AuctionStatus.Active, AuctionStatus.Aftersale];
            case ObjectType.Finished:
                return [AuctionStatus.Active, AuctionStatus.Aftersale, AuctionStatus.Finished, AuctionStatus.New];
        }
    }
    useEffect(() => {
        const handlePageshow = (e) => {
            //reconnect to websocket when coming back from bf-cache
            if (e.persisted) {
                void getAuctionHub();
            }
        };
        const handlePagehide = () => {
            //disconnect from websocket before leaving the page in order for bf-cache to work
            getAuctionHub().then(async (hub) => {
                await (hub === null || hub === void 0 ? void 0 : hub.closeConnection());
            });
        };
        window.addEventListener("pagehide", handlePagehide);
        window.addEventListener("pageshow", handlePageshow);
        void fetchAuctionTypesData();
    }, []);
    useEffect(() => {
        setFilters(true);
    }, [auctions]);
    useEffect(() => {
        setFilters();
    }, [objectsType]);
    useEffect(() => {
        setItems([]);
        void updateSums();
        void fetchItems();
    }, [sortOrder, selectedAuction]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.itemsForSale },
            React.createElement("h2", null, objectsType.value),
            React.createElement("div", { className: styles.info },
                React.createElement("div", { className: styles.sums },
                    React.createElement("label", null,
                        strings.sumBidsText,
                        ":",
                        " ",
                        React.createElement("span", null, formatPriceWs(sums === null || sums === void 0 ? void 0 : sums.currentBidsSum))),
                    React.createElement("label", null,
                        strings.sumEstimatedText,
                        ":",
                        " ",
                        React.createElement("span", null, formatPriceWs(sums === null || sums === void 0 ? void 0 : sums.estimatedSum))),
                    React.createElement("label", null,
                        `${strings.numberOfLotsText}: `,
                        React.createElement("span", null, sums === null || sums === void 0 ? void 0 : sums.numberOfLots)),
                    React.createElement("label", null,
                        `${strings.numberOfBidsText}: `,
                        React.createElement("span", null, sums === null || sums === void 0 ? void 0 : sums.bidsCount))),
                React.createElement("div", { className: styles.filtersContainer },
                    React.createElement("div", { className: styles.selectList },
                        React.createElement("label", null,
                            strings.objectsTypesLabel,
                            ":"),
                        React.createElement("select", { onChange: onObjectsTypesChange, disabled: loading, value: objectsType.key }, viewModel.objectsTypes.map((itm) => (React.createElement("option", { key: itm.key, value: itm.key }, itm.value))))),
                    React.createElement("div", { className: styles.selectList },
                        React.createElement("label", null,
                            strings.sortTypesLabel,
                            ":"),
                        React.createElement("select", { onChange: (e) => onSortOrderChange(e), disabled: loading, value: sortOrder.key }, viewModel.sortOrders.map((itm) => (React.createElement("option", { key: itm.key, value: itm.key }, itm.value))))),
                    React.createElement("div", { className: styles.filterList },
                        React.createElement("label", null,
                            strings.filterByLabel,
                            ":"),
                        React.createElement("select", { value: selectedAuction === null || selectedAuction === void 0 ? void 0 : selectedAuction.key, onChange: (e) => onFilterChange(e), disabled: loading }, auctionOptions.map((itm) => (React.createElement("option", { key: itm.key, value: itm.key }, itm.value))))))),
            React.createElement("div", { className: styles.items }, items.length > 0 &&
                items.map((item) => (React.createElement(Item, { key: item.id, item: item, strings: strings, type: objectsType.key })))),
            React.createElement(PlaceHolderMessage, { error: error, itemsCount: items.length, loading: loading, emptyListText: strings.emptyListText }),
            React.createElement(InfinityScroll, { callback: () => fetchItems(true), loading: loading })),
        React.createElement(GoTopButton, { label: strings.backToTopButton })));
}
const PlaceHolderMessage = (props) => {
    return (React.createElement(React.Fragment, null,
        props.error && React.createElement("span", { className: styles.emptyListText }, props.error),
        props.itemsCount == 0 && !props.loading &&
            React.createElement("span", { className: styles.emptyListText }, props.emptyListText)));
};
function getInitialObjectType(viewModel) {
    var _a;
    const objectTypeKey = sessionStorage.getItem('objectType');
    const defaultObjectType = viewModel.objectsTypes[0];
    if (objectTypeKey) {
        return (_a = viewModel.objectsTypes.find(x => x.key === parseInt(objectTypeKey))) !== null && _a !== void 0 ? _a : defaultObjectType;
    }
    return defaultObjectType;
}
function getInitialSortOrder(viewModel) {
    var _a, _b;
    const sortOrderKey = sessionStorage.getItem('sortOrder');
    const defaultSortOrder = (_a = viewModel.sortOrders.find(x => x.key === viewModel.defaultSortOrder)) !== null && _a !== void 0 ? _a : viewModel.sortOrders[1];
    if (sortOrderKey) {
        return (_b = viewModel.sortOrders.find(x => x.key === parseInt(sortOrderKey))) !== null && _b !== void 0 ? _b : defaultSortOrder;
    }
    return defaultSortOrder;
}
function getLotStatuses(objectType, auctionType) {
    switch (objectType) {
        case ObjectType.Active:
            switch (auctionType) {
                case AuctionStatus.Active:
                    return [AuctionItemStatus.Available];
                case AuctionStatus.Aftersale:
                    return [AuctionItemStatus.NotSold];
                default:
                    return [AuctionItemStatus.Available, AuctionItemStatus.NotSold];
            }
        case ObjectType.New:
            switch (auctionType) {
                case AuctionStatus.New:
                    return [AuctionItemStatus.Prepared, AuctionItemStatus.Scheduled];
                default:
                    return [AuctionItemStatus.Prepared, AuctionItemStatus.Scheduled, AuctionItemStatus.NotSold];
            }
        case ObjectType.Finished:
            return [AuctionItemStatus.Completed, AuctionItemStatus.SoldConfirmed];
    }
}
